import React, { useState } from "react";
import { ChevronRight } from "lucide-react";

/* ─────────── Iter 35 — Bento tile primitives ─────────── */

export function BentoTile({ span = "", className = "", featured = false, ...rest }) {
  return (
    <div
      className={`bt-bento-tile ${span} ${featured ? "bt-tile-featured" : ""} ${className}`}
      {...rest}
    />
  );
}

/* Tiny SVG sparkline — accepts a numeric series; renders a smooth polyline. */
export function Sparkline({ data = [], color = "var(--bt-accent)", height = 36, width = 120 }) {
  if (!data || data.length < 2) return <div style={{ height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const stepX = width / (data.length - 1);
  const pts = data.map((v, i) => `${(i * stepX).toFixed(1)},${(height - ((v - min) / span) * height).toFixed(1)}`).join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline
        fill="none"
        stroke={color}
        strokeWidth={1.8}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={pts}
        className="bt-draw-line"
      />
    </svg>
  );
}

/* Metric tile — label, big mono number, optional sparkline, optional context. */
export function MetricTile({ label, value, valueColor, context, sparkline, featured = false, testId }) {
  return (
    <BentoTile featured={featured}>
      <div className="flex items-start justify-between gap-2">
        <p className="bt-label">{label}</p>
        {sparkline && sparkline.length > 1 && (
          <Sparkline data={sparkline} color={valueColor || "var(--bt-accent)"} width={84} height={28} />
        )}
      </div>
      <p
        data-testid={testId}
        className="font-heading bt-num mt-2"
        style={{ fontSize: 28, lineHeight: 1.1, color: valueColor || "var(--bt-text)" }}
      >
        {value}
      </p>
      {context && (
        <p className="text-[11px] text-[var(--bt-text-muted)] mt-2 leading-snug">{context}</p>
      )}
    </BentoTile>
  );
}

/* Insight tile — clickable; expands inline OR opens a drawer when
   `onClick` is supplied (overrides the inline expand). */
export function InsightTile({ icon, title, summary, tone = "good", detail, testId, defaultOpen = false, onClick }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const accentCls =
    tone === "bad"  ? "bt-insight-accent-bad"  :
    tone === "warn" ? "bt-insight-accent-warn" :
    tone === "muted" ? "bt-insight-accent-muted" :
                      "bt-insight-accent-good";
  // When `onClick` is provided, the tile becomes a drawer trigger — the
  // inline-expand branch is suppressed so the caller controls the detail
  // surface (richer drawer in Dashboard.jsx).
  const handleClick = onClick ? onClick : () => setOpen((o) => !o);
  const showInlineDetail = !onClick && open && detail;
  return (
    <BentoTile className={accentCls}>
      <button
        type="button"
        data-testid={testId}
        onClick={handleClick}
        aria-expanded={onClick ? undefined : open}
        aria-haspopup={onClick ? "dialog" : undefined}
        className="w-full text-left flex items-start gap-3 focus:outline-none rounded-sm"
      >
        <span aria-hidden="true" className="text-xl mt-0.5">{icon}</span>
        <div className="flex-1 min-w-0">
          <p className="bt-label">{title}</p>
          <p className="text-sm text-[var(--bt-text)] mt-1 leading-snug">{summary}</p>
        </div>
        <ChevronRight
          className={`h-4 w-4 mt-1 text-[var(--bt-text-muted)] transition-transform duration-200 ${(!onClick && open) ? "rotate-90" : ""}`}
        />
      </button>
      {showInlineDetail && (
        <div data-testid={testId + "-detail"} className="mt-3 pt-3 border-t border-[var(--bt-border)] text-sm text-[var(--bt-text-muted)] leading-relaxed">
          {detail}
        </div>
      )}
    </BentoTile>
  );
}
